import {
  CYCLE_ID_HEADER,
  CYCLE_TOKEN_HEADER,
  SLOT_HEADER,
  STAGE_ENV_HEADER,
  STAGE_ENV_VALUE,
} from "./constants";
import { readCycleId, readCycleToken, readSlot } from "./session";

/**
 * Builds the header set that tells grm-bff (and, downstream, catalog /
 * store-front / promotions) to serve the tier the current preview session is
 * looking at.
 *
 * <p>Single source of truth for every place that has to stamp those headers:
 * the bootstrap `fetch` / XHR interceptors, and the PDP marketing-text iframe,
 * which owns a separate `window` the interceptors cannot patch.
 *
 * <p>SSR-safe and live-safe: returns an empty object when the session holds
 * neither a cycle id nor a slot, so callers can spread it unconditionally.
 *
 * @return header name → value map; empty when the session is live.
 */
export function readPreviewRequestHeaders(): Record<string, string> {
  const cycleId = readCycleId();
  const slot = readSlot();
  if (cycleId === null && slot === null) {
    return {};
  }

  const headers: Record<string, string> = { [STAGE_ENV_HEADER]: STAGE_ENV_VALUE };

  if (cycleId !== null) {
    // HMAC-signed token takes precedence when present; legacy cycle-id header
    // kept for backwards compatibility with grm-bff instances that haven't
    // shipped the token endpoint yet.
    const token = readCycleToken();
    if (token !== null && token.length > 0) {
      headers[CYCLE_TOKEN_HEADER] = token;
    } else {
      headers[CYCLE_ID_HEADER] = cycleId;
    }
  }

  // Slot header — n-projects preview. Routes downstream services to read
  // from germanos_pre_staging_NN. Stamped independently of cycleId — a slot
  // preview can run without a staging cycle token (admin-only feature).
  if (slot !== null) {
    headers[SLOT_HEADER] = String(slot);
  }

  return headers;
}
